import React, { useContext } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useRemoteData } from 'lib/RemoteData'
import { API_HOST } from 'lib'
import { CommonAssortmentDialog } from 'components/CommonAssortmentDialog'
import { FileStreamOverviewDTO } from '../file-stream-overview/FileStreamOverview'
import { CommonConfig } from 'domain/DTO'
import { SelectedCountryContext } from 'domain/SelectedCountryContext'
import { SRD } from 'srd'
import { updateCommonAssortment } from '../../services/CommonAssortmentService'
import { ArrowRightIcon } from '../../icons'
import HomeIcon from '../../icons/HomeIcon'
import { hasWriteCommonAssortmentPermission, hasWriteInstallationPermission } from '../../lib/Permission'
import { LoadingSpinner } from '../../components/LoadingSpinner'

export const CommonAssortmentEdit = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  if (!id) {
    throw new Error('Missing parameter "id"')
  }
  const { selectedCountry } = useContext(SelectedCountryContext)

  const { data: commonRemoteData, refresh: refreshCommon } = useRemoteData<CommonConfig>(`${API_HOST}/v1/common/${id}`)

  const { data: commonAssortmentsRemoteData, refresh: refreshCommonAssortments } = useRemoteData<CommonConfig[]>(
    `${API_HOST}/v1/common?country=${selectedCountry}`,
  )

  const { data: fileStreamsRemoteData } = useRemoteData<FileStreamOverviewDTO[]>(
    `${API_HOST}/v1/filestream?country=${selectedCountry}`,
  )

  const combinedRemoteData = SRD.map3(
    (common, commons, fileStreams) => ({ common, commonAssortments: commons, fileStreams }),
    commonRemoteData,
    commonAssortmentsRemoteData,
    fileStreamsRemoteData,
  )

  return (
    (hasWriteInstallationPermission() || hasWriteCommonAssortmentPermission()) &&
    SRD.match(
      {
        notAsked: () => <LoadingSpinner />,
        loading: () => <LoadingSpinner />,
        failure: (err) => (
          <div className="card">
            <div className="centerAbsolute">
              <div className="alert alert-danger">
                Uh-oh.. Failed to load Common Assortment Details
                <div>{err.body?.title}</div>
              </div>
            </div>
          </div>
        ),
        success: ({ common, commonAssortments, fileStreams }) => (
          <div>
            <div className="py-md px-lg flex items-center space-x-md">
              <Link aria-label="Home" to={'/'}>
                <HomeIcon size="1.5rem" />
              </Link>
              <ArrowRightIcon size="1rem" />
              <Link to="/common-assortment">
                <h2>Common</h2>
              </Link>
              <ArrowRightIcon size="1rem" />
              <Link to={'/common-assortment/' + common.commonAssortmentId}>
                <h2>{common.name}</h2>
              </Link>
              <ArrowRightIcon size="1rem" />
              <h2>Edit</h2>
            </div>

            <CommonAssortmentDialog
              fileStreams={fileStreams.map((fs) => ({
                name: fs.name,
                id: fs.streamId,
                type: fs.fileType,
                parsedVersion: fs.parsedVersion,
              }))}
              commonAssortments={commonAssortments}
              editing={{
                id: common.commonAssortmentId,
                name: common.name,
                country: selectedCountry,
                concurrencyLimit: common.concurrencyLimit === null ? undefined : common.concurrencyLimit,
                maintenanceWindow: common.maintenanceWindow,
                lockout: common.lockout,
                installationScopes: common.installationScopes,
                scopeFilters: common.scopeFilters,
                shapeStreamId: common.shapeFileStream ? common.shapeFileStream.id : undefined,
                barcodeStreamId: common.barcodeFileStream ? common.barcodeFileStream.id : undefined,
                barcode2StreamId: common.barcodeFileStream ? common.barcode2FileStream?.id : undefined,
                crateStreamId: common.crateFileStream ? common.crateFileStream.id : undefined,
                artikeldatenStreamId: common.artikeldatenFileStream ? common.artikeldatenFileStream.id : undefined,
              }}
              onComplete={(fields) => {
                return updateCommonAssortment(id, fields).then(() => {
                  refreshCommon()
                  refreshCommonAssortments()
                  navigate('/common-assortment/' + common.commonAssortmentId)
                })
              }}
            />
          </div>
        ),
      },
      combinedRemoteData,
    )
  )
}
